/* eslint-disable complexity */
import React, { ReactNode, useCallback, useState } from 'react';

import { ConsolidatedForecastSubEntitySettingsItem } from '@futrli-sdk/consolidations';
import { useCrudActivityContext } from '@futrli/shared-services';
import { UILoader } from '@futrli/ui-components';

import { BasicInfo } from '../../../components/basic-info/basic-info';
import { DeletePredictionDialog } from '../../../components/delete-prediction/delete-prediction';
import { ImpactPreview } from '../impact-preview/impact-preview';
import { PaymentTreatment, PredictionType } from '../../../types';
import { PredictionForm } from '../../../components/prediction-form/prediction-form';
import { useConsolidatedForecastContext } from '../../../../../forecast/detail/context/consolidated-forecast-context';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../../context/consolidated-organisation-prediction-builder-context';
import { useConsolidationDataContext } from '../../../../../predict-management/context/consolidation-data-context';

import { PredictionFormContainer } from '../../../components/prediction-form-container/prediction-form-container';

interface PredictionEditorProps {
  children?: ReactNode;
  footerContent?: ReactNode;
  formError?: boolean;
  onSave?: () => void;
  onSaveAndNew?: () => void;
  show?: boolean;
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const PredictionEditor = ({
  children,
  footerContent,
  formError = false,
  onSave,
  onSaveAndNew,
  show = true,
  onClose,
  isPredictionTypeActive
}: PredictionEditorProps) => {
  const { entities } = useConsolidationDataContext();
  const {
    accountsData,
    accountIndex,
    currency,
    entity,
    formulaHasRefs,
    handleAccountIndexChange,
    handleDeletePrediction,
    handleEntityChange,
    handlePredictionNameChange,
    handleVatPercentageChange,
    handleAddNewMultiPaymentTreatment,
    handleChangeMultiPaymentTreatmentData,
    handleNonCashAccountIndexChange,
    handlePaymentTermsChange,
    handlePaymentTreatmentChange,
    isEditing,
    isSaveDisabled,
    multiPaymentData,
    nonCashAccountIndex,
    paymentTerms,
    paymentTreatment,
    predictionContentTouched,
    predictionName,
    predictionType,
    vatPercentage
  } = useConsolidatedOrganisationPredictionBuilderContext();

  const { addDeleting, isBusy } = useCrudActivityContext();
  const { forecast } = useConsolidatedForecastContext();

  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

  const entitySettings = forecast?.sub_entity_settings as Record<string, ConsolidatedForecastSubEntitySettingsItem>;
  const defaultVatRate = entity ? entitySettings[entity.id]?.vat_rate : undefined;

  const handleDeleteConfirm = useCallback(async () => {
    setIsOpenDeleteDialog(false);
    addDeleting('prediction', handleDeletePrediction());
  }, [addDeleting, handleDeletePrediction]);

  const handleDeleteClick = useCallback(() => setIsOpenDeleteDialog(true), []);

  const handleCloseDeleteDialog = useCallback(() => setIsOpenDeleteDialog(false), []);

  if (isBusy) {
    if (isEditing || isPredictionTypeActive) {
      return <UILoader />;
    }
    return null;
  }

  if (!entities || !show) {
    return null;
  }

  return (
    <PredictionFormContainer
      footerContent={footerContent}
      onClose={onClose}
      onSave={onSave}
      onSaveAndNew={onSaveAndNew}
      onDelete={handleDeleteClick}
      isEditing={isEditing}
      saveDisabled={isSaveDisabled}
    >
      <div>
        <BasicInfo
          predictionName={predictionName}
          accountIndex={accountIndex}
          entities={entities}
          entity={entity}
          entityFieldDisabled={predictionType === PredictionType.FORMULA && formulaHasRefs}
          handlePredictionNameChange={handlePredictionNameChange}
          handleVatPercentageChange={handleVatPercentageChange}
          accountRows={accountsData}
          vatPercentage={vatPercentage}
          currency={currency}
          defaultVatRate={defaultVatRate}
          vatDisabled={paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER}
          predictionContentTouched={predictionContentTouched}
          handleAccountIndexAutocompleteChange={handleAccountIndexChange}
          handleEntityAutocompleteChange={handleEntityChange}
        />
        <PredictionForm
          accountsData={accountsData}
          formError={formError}
          onMultiPaymentDataAddNew={handleAddNewMultiPaymentTreatment}
          onMultiPaymentDataChange={handleChangeMultiPaymentTreatmentData}
          onNonCashAccountIndexChange={handleNonCashAccountIndexChange}
          onPaymentTermsChange={handlePaymentTermsChange}
          onPaymentTreatmentChange={handlePaymentTreatmentChange}
          multiPaymentData={multiPaymentData}
          nonCashAccountIndex={nonCashAccountIndex}
          paymentTerms={paymentTerms}
          paymentTreatment={paymentTreatment}
          predictionContentTouched={predictionContentTouched}
        >
          {children}
        </PredictionForm>
        <ImpactPreview />
        <DeletePredictionDialog
          isOpenDeleteDialog={isOpenDeleteDialog}
          handleToggleDeleteDialog={handleCloseDeleteDialog}
          onSubmit={handleDeleteConfirm}
        />
      </div>
    </PredictionFormContainer>
  );
};
